import { useAuth } from "@frontegg/react";
import Role, { ROLE_RANK, getRoleRank, isMyOnlyRole } from "../enums/Role";
import { getImpersonationFromStorage } from "../contexts/ImpersonationContext";

/**
 * useCheckUserRole
 *
 * Checks if the logged-in user matches a given role, or a range of roles.
 *
 * @note Role only. This hook runs in app.tsx, before Apollo, so it cannot
 *       resolve the backend user id and cannot answer "is this record mine".
 *       For that, compare `useGetLoggedInUser().user.id` to the record's owner
 *       (see ReimbursementDetails). An `isSelf` option used to live here and
 *       compared the auth-provider id against a backend primary key, so it was
 *       never true; OUT-1557 restores the two payments call sites that wanted it.
 *
 * @param role - The base role to check against.
 * @param options - Options for role comparison:
 *   @property {boolean} [orHigher=true] - If true (default), checks for the given role or any higher role in the hierarchy.
 *   @property {boolean} [orLower=false] - If true, checks for the given role or any lower role in the hierarchy. If set, orHigher is ignored.
 *   @property {boolean} [exact=false] - If true, only matches the exact role.
 *
 * @returns {boolean} True if the user matches the role criteria.
 *
 * @example
 * // Manager or higher
 * useCheckUserRole(Role.Manager)
 *
 * // Manager or lower
 * useCheckUserRole(Role.Manager, { orLower: true })
 *
 * // Only Manager
 * useCheckUserRole(Role.Manager, { exact: true })
 */

export type CheckUserRoleOptions = {
  orHigher?: boolean;
  orLower?: boolean;
  exact?: boolean;
};

const useEffectiveRoles = (): { key: string }[] | null => {
  const { user } = useAuth();

  // While impersonating, the target's role decides access, and a target with no role has none.
  const impersonationSession = getImpersonationFromStorage();
  if (impersonationSession) {
    const impersonatedRole = impersonationSession.target?.role;
    return impersonatedRole ? [{ key: impersonatedRole }] : [];
  }

  return Array.isArray(user?.roles) ? user.roles : null;
};

/** True when every role the user holds is /my-only, so they have no admin pages. */
export const useIsMyOnlyRole = (): boolean => {
  const effectiveRoles = useEffectiveRoles();
  return (
    !effectiveRoles || effectiveRoles.every(({ key }) => isMyOnlyRole(key))
  );
};

// defaults to checking for role or higher
export const useCheckUserRole = (
  role: Role,
  {
    orHigher = true,
    orLower = false,
    exact = false,
  }: CheckUserRoleOptions = {},
): boolean => {
  const effectiveRoles = useEffectiveRoles();

  if (!effectiveRoles) return false;

  // Rank semantics: LOWER number = HIGHER authority (see ROLE_RANK).
  const targetRank = getRoleRank(role);

  return effectiveRoles.some(({ key }: { key: string }) => {
    const userRole = key as Role;
    // exact matches the specific role, regardless of rank ties
    if (exact) return userRole === role;
    if (!(userRole in ROLE_RANK)) return false;
    const userRank = getRoleRank(userRole);
    // orLower: the given role or any lower-authority role
    if (orLower) return userRank >= targetRank;
    // orHigher (default): the given role or any higher-authority role
    if (orHigher) return userRank <= targetRank;
    return userRole === role;
  });
};
